import { Skeleton } from '@any-ui-react/core';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';

import { PageTitle } from '@/common/components/shared/layout';
import { Spinner } from '@/common/components/shared/spinner';
import { useCurrentEmployeeContext } from '@/common/contexts';
import { EmployeeRole } from '@/common/graphql';

import { EmployeeAlert, EmployeeDetails, EmployeeIdp, PerformanceReview, ProbationReview } from './components';
import { DashboardSection } from './components/DashboardSection';
import { useGetDashboardEmployeeAlertQuery, useGetDashboardEmployeeQuery } from './graphql';

const getGreetingByTime = () => {
  const currentHour = new Date().getHours();
  if (currentHour < 5) {
    return 'Hi';
  } else if (currentHour >= 5 && currentHour < 12) {
    return 'Good morning';
  } else if (currentHour <= 17) {
    return 'Good afternoon';
  } else {
    return 'Good evening';
  }
};

export const Dashboard = () => {
  const [greeting, setGreeting] = useState(getGreetingByTime());

  useEffect(() => {
    const id = window.setInterval(() => {
      setGreeting(getGreetingByTime());
    }, 5000);

    return () => window.clearInterval(id);
  }, []);

  const { currentEmployee } = useCurrentEmployeeContext();

  const { data: dataEmployee, loading: loadingEmployee } = useGetDashboardEmployeeQuery();
  const { data: dataAlert, loading: loadingAlert } = useGetDashboardEmployeeAlertQuery({
    skip: ![EmployeeRole.SuperAdmin, EmployeeRole.AdminPrivate, EmployeeRole.AdminStandard].includes(
      currentEmployee?.permission?.role ?? EmployeeRole.Basic,
    ),
  });

  const showMyPerformanceReviews = useMemo(
    () => (dataEmployee?.currentEmployee.employee.performanceReviews.length ?? 0) > 0,
    [dataEmployee?.currentEmployee.employee.performanceReviews.length],
  );
  const showMyProbationReview = useMemo(
    () =>
      (dataEmployee?.currentEmployee.employee.generalInformation.needsProbationReview ?? true) &&
      !!dataEmployee?.currentEmployee.employee.probationReview,
    [dataEmployee?.currentEmployee.employee],
  );
  const showDirectReportsPerformanceReviews = useMemo(
    () => (dataEmployee?.listDirectReportsPerformanceReviews?.length ?? 0) > 0,
    [dataEmployee?.listDirectReportsPerformanceReviews?.length],
  );
  const showDirectReportsProbationReviews = useMemo(
    () => (dataEmployee?.listDirectReportsProbationReviews?.length ?? 0) > 0,
    [dataEmployee?.listDirectReportsProbationReviews?.length],
  );
  const showEmployeeIdpWidget = useMemo(
    () => (dataEmployee?.employeeIdpDashboardWidget?.length ?? 0) > 0,
    [dataEmployee?.employeeIdpDashboardWidget?.length],
  );

  return (
    <>
      <PageTitle
        aside={() =>
          loadingEmployee ? (
            <Skeleton className="h-6 w-60" />
          ) : (
            <span className="hidden text-lg md:flex">
              {greeting}
              ,&nbsp;
              {dataEmployee?.currentEmployee?.employee?.generalInformation?.preferredFullName}
              &nbsp; 👋
            </span>
          )
        }
      />

      {loadingEmployee || !dataEmployee ? (
        <div className="flex justify-center items-center h-full">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {showMyPerformanceReviews && (
            <PerformanceReview
              className={classNames('col-span-2', { 'md:col-span-1': showMyProbationReview })}
              employeeId={dataEmployee.currentEmployee.employee.id}
              loading={loadingEmployee}
              performanceReviews={dataEmployee.currentEmployee.employee.performanceReviews}
              showPeriodFilter={false}
              title="My Performance Review"
              withName={false}
            />
          )}

          {showMyProbationReview && (
            <ProbationReview
              className="col-span-2 md:col-span-1"
              employeeId={dataEmployee.currentEmployee.employee.id}
              loading={loadingEmployee}
              mode="self"
              probationReview={
                dataEmployee.currentEmployee.employee.probationReview
                  ? [dataEmployee.currentEmployee.employee.probationReview]
                  : []
              }
              title="My Probation Review"
            />
          )}

          {showDirectReportsPerformanceReviews && (
            <PerformanceReview
              className="col-span-2 xl:col-span-1"
              loading={loadingEmployee}
              performanceReviews={dataEmployee.listDirectReportsPerformanceReviews}
              showPeriodFilter={true}
              title="Your members' Performance Review"
              withName={true}
            />
          )}

          {showDirectReportsProbationReviews && (
            <ProbationReview
              className="col-span-2 xl:col-span-1"
              loading={loadingEmployee}
              mode="reviewer"
              probationReview={dataEmployee.listDirectReportsProbationReviews}
              title="Your members' Probation Review"
            />
          )}

          {showEmployeeIdpWidget && (
            <EmployeeIdp
              className="col-span-2 xl:col-span-1"
              currentEmployeeId={dataEmployee.currentEmployee.employee.id}
              data={dataEmployee.employeeIdpDashboardWidget}
              loading={loadingEmployee}
            />
          )}

          <EmployeeAlert className="col-span-2" employeeAlert={dataAlert} loading={loadingAlert} />

          <DashboardSection
            className="col-span-2"
            link={`/employees/edit/${dataEmployee.currentEmployee.employee.id}`}
            title="My Information"
          >
            <EmployeeDetails employee={dataEmployee.currentEmployee.employee} />
          </DashboardSection>
        </div>
      )}
    </>
  );
};
